import { useCallback, useEffect, useMemo, useState } from "react";
import { getAdminLocationQuality } from "../../../services/midpointApi";
import { CardError, CardLoading } from "../intelligence/CardState";
import AdminBtn from "./layout/AdminBtn";
import AdminPageHeader from "./layout/AdminPageHeader";
import AdminPageLayout from "./layout/AdminPageLayout";
import RealEstateAdminNav, { buildRealEstateAdminNavLinks } from "./layout/RealEstateAdminNav";

const STATUS_META = {
  EXACT: {
    emoji: "📍",
    label: "Exact pin",
    hint: "Precise coordinates — safe to show on the map.",
    tone: "good",
  },
  GEOCODED: {
    emoji: "✅",
    label: "Geocoded",
    hint: "Address was geocoded successfully.",
    tone: "good",
  },
  PENDING_GEOCODE: {
    emoji: "⏳",
    label: "In geocode queue",
    hint: "Waiting for the geocoder — check the queue for progress.",
    tone: "wait",
  },
  FALLBACK_APPLIED: {
    emoji: "📌",
    label: "Approximate area",
    hint: "Placed on a neighborhood or city centroid, not the exact address.",
    tone: "warn",
  },
  UNMAPPED: {
    emoji: "❓",
    label: "No map pin",
    hint: "Missing coordinates — won't appear correctly on the map.",
    tone: "bad",
  },
  FAILED: {
    emoji: "⚠️",
    label: "Geocode failed",
    hint: "Geocoding was attempted but did not resolve.",
    tone: "bad",
  },
  UNKNOWN: {
    emoji: "·",
    label: "Unknown",
    hint: "Status not set.",
    tone: "neutral",
  },
};

const FILTER_PRESETS = [
  {
    id: "all",
    emoji: "🗂️",
    label: "All listings",
    description: "Everything active in the catalog",
  },
  {
    id: "unmapped",
    emoji: "❓",
    label: "No map pin",
    description: "Listings missing coordinates",
    geocodeStatus: "",
    unmappedOnly: true,
    approximateOnly: false,
  },
  {
    id: "pending",
    emoji: "⏳",
    label: "In queue",
    description: "Waiting to be geocoded",
    geocodeStatus: "PENDING_GEOCODE",
    unmappedOnly: false,
    approximateOnly: false,
  },
  {
    id: "approximate",
    emoji: "📌",
    label: "Approximate",
    description: "Fallback or low-precision placement",
    geocodeStatus: "",
    unmappedOnly: false,
    approximateOnly: true,
  },
  {
    id: "failed",
    emoji: "⚠️",
    label: "Failed",
    description: "Geocode errors to investigate",
    geocodeStatus: "FAILED",
    unmappedOnly: false,
    approximateOnly: false,
  },
];

function fmt(n) {
  if (n == null) return "—";
  return Number(n).toLocaleString();
}

function pct(part, total) {
  if (!total) return 0;
  return Math.round((part / total) * 100);
}

function resolveStatusMeta(status) {
  const key = String(status || "UNKNOWN").toUpperCase();
  return STATUS_META[key] || STATUS_META.UNKNOWN;
}

function healthEmoji(rate) {
  if (rate >= 90) return "🟢";
  if (rate >= 70) return "🟡";
  return "🔴";
}

function HealthBanner({ totals, mappedRate, onOpenGeocodeQueue }) {
  const total = totals.total_active ?? 0;
  const unmapped = totals.unmapped ?? 0;
  const pending = totals.pending_geocode ?? 0;
  const failed = totals.geocode_failed ?? 0;
  const needsAttention = unmapped + pending + failed;

  return (
    <section className="lq-hero panel-card">
      <div className="lq-hero__main">
        <div className="lq-hero__score" aria-label={`Map coverage ${mappedRate ?? 0} percent`}>
          <span className="lq-hero__score-emoji" aria-hidden="true">
            {healthEmoji(mappedRate ?? 0)}
          </span>
          <div>
            <p className="lq-hero__eyebrow">Map coverage</p>
            <strong className="lq-hero__value">{mappedRate != null ? `${mappedRate}%` : "—"}</strong>
            <p className="lq-hero__sub">
              {fmt(totals.mapped_total ?? 0)} of {fmt(total)} listings have coordinates
            </p>
          </div>
        </div>
        <p className="lq-hero__explain">
          Listings need trustworthy coordinates before they show up correctly on CasaMapa and buyer
          search maps. Fix unmapped and failed items first — approximate pins are usable but less
          precise.
        </p>
      </div>
      <div className="lq-hero__actions">
        {needsAttention > 0 ? (
          <p className="lq-hero__attention">
            <span aria-hidden="true">👀</span> {fmt(needsAttention)} listing
            {needsAttention === 1 ? "" : "s"} need attention
          </p>
        ) : (
          <p className="lq-hero__attention lq-hero__attention--good">
            <span aria-hidden="true">✨</span> All active listings are mapped
          </p>
        )}
        {onOpenGeocodeQueue ? (
          <AdminBtn variant="primary" onClick={onOpenGeocodeQueue}>
            ⏳ Open geocode queue
          </AdminBtn>
        ) : null}
      </div>
    </section>
  );
}

function KpiStrip({ totals, activePreset, onSelectPreset }) {
  const cards = [
    {
      emoji: "🏠",
      label: "Active",
      value: totals.total_active ?? 0,
      readOnly: true,
    },
    {
      emoji: "✅",
      label: "High confidence",
      value: totals.mapped_high_confidence ?? 0,
      sub: "Exact or geocoded",
      readOnly: true,
    },
    {
      preset: "approximate",
      emoji: "📌",
      label: "Approximate",
      value: totals.mapped_approximate ?? 0,
    },
    {
      preset: "unmapped",
      emoji: "❓",
      label: "Unmapped",
      value: totals.unmapped ?? 0,
      tone: (totals.unmapped ?? 0) > 0 ? "bad" : null,
    },
    {
      preset: "pending",
      emoji: "⏳",
      label: "In queue",
      value: totals.pending_geocode ?? 0,
      tone: (totals.pending_geocode ?? 0) > 0 ? "wait" : null,
    },
    {
      preset: "failed",
      emoji: "⚠️",
      label: "Failed",
      value: totals.geocode_failed ?? 0,
      tone: (totals.geocode_failed ?? 0) > 0 ? "bad" : null,
    },
  ];

  return (
    <section className="lq-kpis" aria-label="Location quality summary">
      {cards.map((card) => {
        const className = `lq-kpi panel-card${!card.readOnly && activePreset === card.preset ? " lq-kpi--active" : ""}${card.tone ? ` lq-kpi--${card.tone}` : ""}${card.readOnly ? " lq-kpi--readonly" : ""}`;
        const content = (
          <>
            <span className="lq-kpi__emoji" aria-hidden="true">
              {card.emoji}
            </span>
            <span className="lq-kpi__label">{card.label}</span>
            <strong className="lq-kpi__value">{fmt(card.value)}</strong>
            {card.sub ? <span className="lq-kpi__sub">{card.sub}</span> : null}
          </>
        );

        if (card.readOnly) {
          return (
            <div key={card.label} className={className}>
              {content}
            </div>
          );
        }

        return (
          <button
            key={card.label}
            type="button"
            className={className}
            onClick={() => onSelectPreset(card.preset)}
          >
            {content}
          </button>
        );
      })}
    </section>
  );
}

function CoverageBreakdown({ totals }) {
  const total = totals.total_active ?? 0;
  if (!total) return null;

  const segments = [
    {
      key: "high",
      label: "High confidence",
      emoji: "✅",
      count: totals.mapped_high_confidence ?? 0,
      className: "lq-breakdown__seg--good",
    },
    {
      key: "approx",
      label: "Approximate",
      emoji: "📌",
      count: totals.mapped_approximate ?? 0,
      className: "lq-breakdown__seg--warn",
    },
    {
      key: "unmapped",
      label: "Unmapped",
      emoji: "❓",
      count: totals.unmapped ?? 0,
      className: "lq-breakdown__seg--bad",
    },
    {
      key: "pending",
      label: "In queue",
      emoji: "⏳",
      count: totals.pending_geocode ?? 0,
      className: "lq-breakdown__seg--wait",
    },
    {
      key: "failed",
      label: "Failed",
      emoji: "⚠️",
      count: totals.geocode_failed ?? 0,
      className: "lq-breakdown__seg--bad",
    },
  ].filter((segment) => segment.count > 0);

  return (
    <section className="lq-breakdown panel-card">
      <h2 className="lq-section-title">
        <span aria-hidden="true">📊</span> How listings break down
      </h2>
      <div className="lq-breakdown__bar" role="img" aria-label="Location quality distribution">
        {segments.map((segment) => (
          <div
            key={segment.key}
            className={`lq-breakdown__seg ${segment.className}`}
            style={{ width: `${Math.max(pct(segment.count, total), segment.count > 0 ? 4 : 0)}%` }}
            title={`${segment.label}: ${segment.count}`}
          />
        ))}
      </div>
      <ul className="lq-breakdown__legend">
        {segments.map((segment) => (
          <li key={segment.key}>
            <span aria-hidden="true">{segment.emoji}</span>
            {segment.label}
            <strong>{fmt(segment.count)}</strong>
            <span className="lq-breakdown__pct">({pct(segment.count, total)}%)</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function SourceHealthTable({ rows, onFilterSource }) {
  if (!rows?.length) return null;

  return (
    <section className="lq-sources panel-card">
      <h2 className="lq-section-title">
        <span aria-hidden="true">🏢</span> By broker source
      </h2>
      <p className="lq-section-note">Sources with the most unmapped or approximate listings float to the top.</p>
      <div className="lq-sources__table-wrap">
        <table className="lq-sources__table">
          <thead>
            <tr>
              <th>Source</th>
              <th>Health</th>
              <th>Total</th>
              <th>Unmapped</th>
              <th>Approximate</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const mapped = Math.max(row.total - row.unmapped, 0);
              const healthRate = pct(mapped, row.total);
              return (
                <tr key={row.source_code}>
                  <td>
                    <button
                      type="button"
                      className="lq-sources__name"
                      onClick={() => onFilterSource(row.source_code)}
                    >
                      {row.source_name || row.source_code}
                    </button>
                    <span className="lq-sources__code">{row.source_code}</span>
                  </td>
                  <td>
                    <div className="lq-sources__health">
                      <div className="lq-sources__health-track">
                        <div
                          className="lq-sources__health-fill"
                          style={{ width: `${healthRate}%` }}
                        />
                      </div>
                      <span>{healthRate}% mapped</span>
                    </div>
                  </td>
                  <td>{fmt(row.total)}</td>
                  <td className={row.unmapped > 0 ? "lq-cell--bad" : ""}>{fmt(row.unmapped)}</td>
                  <td className={row.approximate > 0 ? "lq-cell--warn" : ""}>{fmt(row.approximate)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ListingsTable({ listings }) {
  if (!listings.length) {
    return (
      <section className="lq-listings panel-card">
        <h2 className="lq-section-title">
          <span aria-hidden="true">📋</span> Listings
        </h2>
        <p className="lq-empty">No listings match this filter. Try another preset above.</p>
      </section>
    );
  }

  return (
    <section className="lq-listings panel-card">
      <h2 className="lq-section-title">
        <span aria-hidden="true">📋</span> Listings <span className="lq-count">({listings.length})</span>
      </h2>
      <div className="lq-listings__table-wrap">
        <table className="lq-listings__table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Listing</th>
              <th>Where</th>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            {listings.map((listing) => {
              const meta = resolveStatusMeta(listing.geocodeStatus);
              const place = [listing.neighborhood, listing.city || listing.municipality]
                .filter(Boolean)
                .join(", ");

              return (
                <tr key={listing.propertyId}>
                  <td>
                    <div className={`lq-status lq-status--${meta.tone}`} title={meta.hint}>
                      <span className="lq-status__emoji" aria-hidden="true">
                        {meta.emoji}
                      </span>
                      <span className="lq-status__label">{meta.label}</span>
                    </div>
                  </td>
                  <td>
                    <div className="lq-listing__title">{listing.title || "(Untitled listing)"}</div>
                    <div className="lq-listing__id">{listing.externalPropertyId}</div>
                    {listing.detailUrl ? (
                      <a
                        className="lq-listing__link"
                        href={listing.detailUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View original ↗
                      </a>
                    ) : null}
                  </td>
                  <td>
                    <div>{place || "—"}</div>
                    {listing.coordinateLabel ? (
                      <div className="lq-listing__meta">{listing.coordinateLabel}</div>
                    ) : null}
                    {listing.isApproximateLocation ? (
                      <div className="lq-listing__meta">📌 Approximate placement</div>
                    ) : null}
                    {listing.geocodeError ? (
                      <div className="lq-listing__meta lq-listing__meta--error">
                        ⚠️ {listing.geocodeError}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <div>{listing.sourceCode}</div>
                    {listing.coordinateSource ? (
                      <div className="lq-listing__meta">{listing.coordinateSource}</div>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const LocationQualityPage = ({
  onClose,
  onOpenGeocodeQueue,
  onOpenCanonicalListings,
  onOpenSourceQuality,
  onOpenImageHealth,
  onOpenImageCache,
  onOpenMediaQualityDashboard,
}) => {
  const [summary, setSummary] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activePreset, setActivePreset] = useState("all");
  const [sourceCode, setSourceCode] = useState("");

  const filterState = useMemo(() => {
    const preset = FILTER_PRESETS.find((item) => item.id === activePreset) || FILTER_PRESETS[0];
    return {
      geocodeStatus: preset.geocodeStatus ?? "",
      unmappedOnly: preset.unmappedOnly ?? false,
      approximateOnly: preset.approximateOnly ?? false,
    };
  }, [activePreset]);

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await getAdminLocationQuality({
        geocodeStatus: filterState.geocodeStatus || undefined,
        sourceCode: sourceCode.trim() || undefined,
        unmappedOnly: filterState.unmappedOnly,
        approximateOnly: filterState.approximateOnly,
        limit: 150,
      });
      setSummary(data.summary || null);
      setListings(data.listings || []);
    } catch (loadError) {
      setSummary(null);
      setListings([]);
      setError(loadError.message || "Unable to load location quality dashboard.");
    } finally {
      setLoading(false);
    }
  }, [filterState, sourceCode]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const totals = summary?.totals || {};
  const bySource = summary?.bySource || [];

  const mappedRate = useMemo(() => {
    if (!totals.total_active) return null;
    return pct(totals.mapped_total, totals.total_active);
  }, [totals]);

  const navLinks = buildRealEstateAdminNavLinks({
    current: "locationQuality",
    onOpenCanonicalListings,
    onOpenGeocodeQueue,
    onOpenSourceQuality,
    onOpenImageHealth,
    onOpenImageCache,
    onOpenMediaQualityDashboard,
  });

  const handleFilterSource = (code) => {
    setSourceCode(code);
    setActivePreset("all");
  };

  return (
    <AdminPageLayout className="location-quality-dashboard">
      <AdminPageHeader
        eyebrow="Administration · Real Estate"
        title="📍 Location Quality"
        description="See which listings are ready for the map — and which still need geocoding."
        onBack={onClose}
        navigation={<RealEstateAdminNav links={navLinks} />}
      />

      {loading ? <CardLoading label="Loading location quality…" /> : null}
      {error ? <CardError message={error} /> : null}

      {!loading && !error ? (
        <>
          <HealthBanner
            totals={totals}
            mappedRate={mappedRate}
            onOpenGeocodeQueue={onOpenGeocodeQueue}
          />

          <KpiStrip
            totals={totals}
            activePreset={activePreset}
            onSelectPreset={setActivePreset}
          />

          <CoverageBreakdown totals={totals} />

          <section className="lq-filters panel-card">
            <h2 className="lq-section-title">
              <span aria-hidden="true">🔎</span> Focus the list
            </h2>
            <div className="lq-filters__presets" role="tablist" aria-label="Listing filters">
              {FILTER_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  role="tab"
                  aria-selected={activePreset === preset.id}
                  className={`lq-filter-chip${activePreset === preset.id ? " is-active" : ""}`}
                  onClick={() => setActivePreset(preset.id)}
                >
                  <span className="lq-filter-chip__emoji" aria-hidden="true">
                    {preset.emoji}
                  </span>
                  <span className="lq-filter-chip__text">
                    <strong>{preset.label}</strong>
                    <span>{preset.description}</span>
                  </span>
                </button>
              ))}
            </div>
            <div className="lq-filters__row">
              <label className="lq-filters__source">
                <span>Broker source</span>
                <input
                  type="search"
                  value={sourceCode}
                  onChange={(event) => setSourceCode(event.target.value)}
                  placeholder="Filter by source code, e.g. MB_INMOBILIARIA"
                />
              </label>
              <AdminBtn variant="secondary" onClick={loadDashboard}>
                ↻ Refresh
              </AdminBtn>
              {sourceCode ? (
                <AdminBtn variant="ghost" onClick={() => setSourceCode("")}>
                  Clear source
                </AdminBtn>
              ) : null}
            </div>
          </section>

          <SourceHealthTable rows={bySource} onFilterSource={handleFilterSource} />

          <ListingsTable listings={listings} />
        </>
      ) : null}
    </AdminPageLayout>
  );
};

export default LocationQualityPage;
